import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, Ban, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { Field, inputClass } from '../../../../components/ui/Field';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { OperatorTabs } from '../../components/OperatorTabs';
import { QuantityStepper } from '../../components/QuantityStepper';
import { SlotSuggestions } from '../../components/SlotSuggestions';
import { openDatePicker } from '../../components/openDatePicker';
import { KitResult } from './KitResult';
import { KitSources } from './KitSources';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../../../../features/operator/operatorConstants';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import {
  KIT_WRITES,
  assembleKit,
  disassembleKit,
  fetchKit,
  fetchKitPlan,
  loadKit,
  reloadKitPlan,
  retryLastKitWrite,
  scanKitCode,
  showKitSources,
  submitKitWrite,
} from '../../../../features/operatorKits/operatorKitsThunks';
import {
  kitEntered,
  kitLotChanged,
  kitModeChanged,
  kitQuantityChanged,
  kitSourcesConfirmed,
  kitStepBack,
  kitWorkRestarted,
  selectKitAccepting,
  selectKitUnconfirmed,
} from '../../../../features/operatorKits/operatorKitsSlice';
import {
  KIT_WORK,
  KIT_WORK_LABELS,
  KIT_STEP,
  disassemblySource,
  givesText,
  kitActionText,
  kitAgainLabel,
  kitBlockedText,
  kitConfirmMessage,
  kitDestinationProblem,
  kitFree,
  kitMaxText,
  kitModeMax,
  kitName,
  kitQuantityProblem,
  kitSourcesProblem,
  kitSubmitLabel,
  kitWriteProblem,
  planLegs,
  sourcesDoneLabel,
} from '../../../../features/operatorKits/operatorKitsUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const MODES = [KIT_WORK.ASSEMBLE, KIT_WORK.DISASSEMBLE];

const busyLabelOf = (mode) => (mode === KIT_WORK.DISASSEMBLE ? 'Desarmando…' : 'Armando…');

/** The kit: what it is, whose, and how many there are. */
const KitHeader = ({ kit }) => {
  const reserved = Number(kit.kit_stock?.reserved) || 0;
  return (
    <section className="space-y-1 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <p className="text-lg font-bold text-ninja-dark dark:text-white">{kitName(kit)}</p>
      <p className="text-sm text-text-secondary">{[kit.name, kit.company?.name].filter(Boolean).join(' · ')}</p>
      <p className="text-sm text-text-secondary">
        En stock: {formatQuantity(kitFree(kit))} {kitFree(kit) === 1 ? 'libre' : 'libres'}
        {reserved > 0 ? ` · ${formatQuantity(reserved)} reservados` : ''}
      </p>
    </section>
  );
};

/** What a kit is made of: how many of each per kit, and what is free of each. Assembling, what limits it shows. */
const KitComponents = ({ kit, mode }) => {
  const limited = mode === KIT_WORK.ASSEMBLE ? kit.limited_by || [] : [];
  return (
    <ul className="space-y-2" aria-label="Componentes del kit">
      {(kit.components || []).map((component) => (
        <li
          key={component.product_id}
          data-testid="kit-component"
          data-product-id={component.product_id}
          data-free={Number(component.free) || 0}
          className={clsx(
            'flex items-start justify-between gap-3 rounded-2xl border bg-white p-3 dark:bg-[#1a1a1a]',
            limited.includes(component.sku) ? 'border-amber-400 dark:border-amber-600' : 'border-border'
          )}
        >
          <span className="min-w-0">
            <span className="block font-bold text-ninja-dark dark:text-white">
              {formatQuantity(component.per_kit)} × {component.sku}
            </span>
            <span className="block truncate text-sm text-text-secondary">{component.name}</span>
          </span>
          <span className="shrink-0 text-right text-sm text-text-secondary">
            {formatQuantity(component.free)} libres
            {limited.includes(component.sku) && (
              <span className="block font-semibold text-amber-700 dark:text-amber-300">Limita</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

/** What the confirmation lists: the sources putting kits together; what comes out taking them apart. */
const confirmItems = (visit) => {
  const { plan, preferred, mode, quantity, kit, destination } = visit;
  if (!plan) return [];
  if (mode === KIT_WORK.DISASSEMBLE) {
    const source = disassemblySource(plan, preferred);
    return [
      ...(source ? [{ key: 'source', title: `${formatQuantity(quantity)} ${kitName(kit)}`, subtitle: `de ${source.slotCode}` }] : []),
      ...(plan.gives || []).map((item) => ({
        key: item.product_id,
        title: `${formatQuantity(item.quantity)} ${item.sku}`,
        subtitle: `a ${destination?.code}`,
      })),
    ];
  }
  return planLegs(plan, preferred).flatMap((component) =>
    component.legs.map((leg) => ({
      key: `${component.productId}:${leg.slotCode}:${leg.lot}`,
      title: `${formatQuantity(leg.quantity)} ${component.sku}`,
      subtitle: [`de ${leg.slotCode}`, leg.lot ? `lote ${leg.lot}` : null].filter(Boolean).join(' · '),
    }))
  );
};

/**
 * Kits: putting kits together or taking them apart, one kit at a time. How
 * many → where it all comes from («Ya los junté») → scan where it is left →
 * confirm. Free work, without a task; the server registers it in the ledger.
 * A lost answer blocks everything but «Reintentar», with the same key.
 */
export const KitDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { kitId: param } = useParams();
  const kitId = /^\d+$/.test(param || '') ? Number(param) : null;
  const visit = useSelector((state) => state.operatorKits);
  const accepting = useSelector(selectKitAccepting);
  const unconfirmed = useSelector(selectKitUnconfirmed);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (kitId === null) {
      dispatch(taskEntered({ flow: OPERATOR_FLOW.KITS, taskId: param }));
      return;
    }
    dispatch(kitEntered(kitId));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.KITS, taskId: kitId }));
    dispatch(loadKit(kitId));
  }, [dispatch, kitId, param]);

  const toStation = () => navigate(OPERATOR_FLOW_PATHS[OPERATOR_FLOW.KITS]);

  if (kitId === null) {
    return (
      <ErrorState title="Ese kit no existe" description="Vuelve a Kits y elígelo de la lista.">
        <Button variant="ghost" onClick={toStation}>
          Volver a Kits
        </Button>
      </ErrorState>
    );
  }
  if (visit.kitId !== kitId) return null;

  const { kit, loading, error, mode, step, pending, destination } = visit;
  if (!kit && loading) {
    return (
      <div className="flex justify-center py-16" data-testid="kit-loading">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (!kit && error) {
    return (
      <div data-testid="kit-error">
        <ErrorState title="No pudimos abrir el kit" description={error.message} onRetry={() => dispatch(fetchKit(kitId))}>
          <Button variant="ghost" onClick={toStation}>
            Volver a Kits
          </Button>
        </ErrorState>
      </div>
    );
  }
  if (!kit) return null;

  const sku = kitName(kit);
  const locked = Boolean(pending) || unconfirmed;
  const blocked = kitBlockedText(kit);
  const modeWrite = mode === KIT_WORK.DISASSEMBLE ? disassembleKit : assembleKit;
  const writeThunk = (visit.lastWrite && KIT_WRITES[visit.lastWrite.type]) || modeWrite;

  const confirm = async () => {
    await dispatch(submitKitWrite());
    setConfirming(false);
  };

  let content = null;
  let bar = null;
  if (step === KIT_STEP.QUANTITY) {
    const problem = kitQuantityProblem(visit.quantity, kit, mode);
    content = (
      <>
        <KitComponents kit={kit} mode={mode} />
        <p
          data-testid="kit-max"
          data-max={kitModeMax(kit, mode)}
          className="text-base font-bold text-ninja-dark dark:text-white"
        >
          {kitMaxText(kit, mode)}
        </p>
        <QuantityStepper
          label={mode === KIT_WORK.DISASSEMBLE ? '¿Cuántos desarmas?' : '¿Cuántos armas?'}
          value={visit.quantity}
          onChange={(value) => dispatch(kitQuantityChanged(value))}
          min={0}
          max={kitModeMax(kit, mode)}
          allowDecimals={Boolean(kit.allows_fractional_qty)}
          unit="kits"
          testId="kit-quantity"
        />
      </>
    );
    bar = (
      <Button
        variant="primary"
        size="lg"
        disabled={Boolean(problem) || locked}
        onClick={() => dispatch(showKitSources())}
        data-testid="kit-next"
      >
        {problem || 'Ver de dónde sale'}
      </Button>
    );
  } else if (step === KIT_STEP.SOURCES) {
    const problem = kitSourcesProblem(visit.plan, visit.preferred, mode, visit.quantity);
    if (visit.planError) {
      content = (
        <div data-testid="kit-plan-error">
          <ErrorState variant="compact" title="No pudimos calcular de dónde sale" description={visit.planError.message} />
        </div>
      );
    } else if (!visit.plan) {
      content = (
        <div className="flex justify-center py-10" data-testid="kit-plan-loading">
          <Loader2 size={28} className="animate-spin text-ninja-green" />
        </div>
      );
    } else {
      content = <KitSources visit={visit} locked={locked} />;
    }
    bar = (
      <>
        <Button variant="ghost" size="lg" disabled={locked} onClick={() => dispatch(kitStepBack())} data-testid="kit-back">
          Volver
        </Button>
        {visit.planError ? (
          <PendingButton
            pendingOn={fetchKitPlan}
            pendingKey={kitId}
            variant="primary"
            size="lg"
            icon={RefreshCw}
            loadingLabel="Calculando…"
            onClick={() => dispatch(reloadKitPlan())}
            data-testid="kit-plan-retry"
          >
            Reintentar
          </PendingButton>
        ) : (
          <PendingButton
            pendingOn={fetchKitPlan}
            pendingKey={kitId}
            variant="primary"
            size="lg"
            disabled={Boolean(problem) || locked}
            loadingLabel="Calculando…"
            onClick={() => dispatch(kitSourcesConfirmed())}
            data-testid="kit-next"
          >
            {problem || sourcesDoneLabel(mode)}
          </PendingButton>
        )}
      </>
    );
  } else if (step === KIT_STEP.DESTINATION) {
    const problem = kitWriteProblem(visit);
    const disassembly = mode === KIT_WORK.DISASSEMBLE;
    content = (
      <>
        <p className="text-base font-bold text-ninja-dark dark:text-white">
          {disassembly
            ? `${kitActionText(mode, visit.quantity, sku)}: salen ${givesText(visit.plan)}`
            : `${kitActionText(mode, visit.quantity, sku)}`}
        </p>
        <SlotSuggestions
          suggestions={visit.plan?.destinations}
          interactive
          disabled={!accepting}
          onTap={(code) => dispatch(scanKitCode({ code, source: SCAN_SOURCE.TAP }))}
          testId="kit-suggestion"
        />
        {destination && (
          <p
            data-testid="kit-destination"
            data-slot-code={destination.code}
            data-suggested={String(destination.suggested)}
            className={clsx(
              'rounded-2xl px-3 py-2 text-sm font-bold',
              destination.suggested ? 'bg-ninja-green text-white' : 'bg-amber-400 text-ninja-dark'
            )}
          >
            Destino: {destination.name || destination.code}
            {destination.suggested ? ' · sugerida' : ' · no es la sugerida'}
          </p>
        )}
        <OperatorScanPanel
          prompt={disassembly ? 'Escanea dónde dejas los componentes' : 'Escanea dónde dejas los kits'}
          hint={
            visit.plan?.destinations?.length > 0
              ? 'O toca una sugerida arriba. Si la etiqueta no se lee, escribe su código abajo.'
              : 'Si la etiqueta no se lee, escribe su código abajo.'
          }
          accepting={accepting}
          lastCode={visit.scan.lastCode}
          holdReads={!unconfirmed}
          busyLabel={unconfirmed ? 'Reintenta antes de seguir' : pending ? busyLabelOf(mode) : 'Buscando…'}
          onCode={(code, source) => dispatch(scanKitCode({ code, source }))}
        />
        {!disassembly && kit.lot_controlled && (
          <Field label="Lote de los kits" htmlFor="kit-lot">
            <input
              id="kit-lot"
              data-testid="kit-lot"
              value={visit.kitLot.lot}
              disabled={locked}
              onChange={(event) => dispatch(kitLotChanged({ field: 'lot', value: event.target.value }))}
              autoComplete="off"
              className={`${inputClass} h-12 text-base`}
            />
          </Field>
        )}
        {!disassembly && kit.expiry_controlled && (
          <Field
            label="Vencimiento de los kits"
            htmlFor="kit-expiry"
            hint={visit.plan?.kit_lot?.suggested_expiry ? `El primero que vence entre sus componentes: ${visit.plan.kit_lot.suggested_expiry}` : undefined}
          >
            <input
              id="kit-expiry"
              data-testid="kit-expiry"
              type="date"
              value={visit.kitLot.expiryDate}
              disabled={locked}
              onChange={(event) => dispatch(kitLotChanged({ field: 'expiryDate', value: event.target.value }))}
              onClick={openDatePicker}
              className={`${inputClass} h-12 text-base dark:[color-scheme:dark]`}
            />
          </Field>
        )}
      </>
    );
    bar = (
      <>
        <Button variant="ghost" size="lg" disabled={locked} onClick={() => dispatch(kitStepBack())} data-testid="kit-back">
          Volver
        </Button>
        {unconfirmed ? (
          <PendingButton
            pendingOn={writeThunk}
            pendingKey={kitId}
            variant="primary"
            size="lg"
            icon={RotateCcw}
            loadingLabel="Reintentando…"
            onClick={() => dispatch(retryLastKitWrite())}
            data-testid="kit-retry"
          >
            Reintentar
          </PendingButton>
        ) : (
          <PendingButton
            pendingOn={writeThunk}
            pendingKey={kitId}
            variant="primary"
            size="lg"
            disabled={Boolean(problem) || Boolean(visit.scanning)}
            loadingLabel={busyLabelOf(mode)}
            onClick={() => setConfirming(true)}
            data-testid="kit-submit"
          >
            {problem || kitSubmitLabel(visit)}
          </PendingButton>
        )}
      </>
    );
  } else if (step === KIT_STEP.RESULT && visit.result) {
    content = <KitResult result={visit.result} />;
    bar = (
      <>
        <Button variant="ghost" size="lg" onClick={toStation} data-testid="kit-other">
          Otro kit
        </Button>
        <Button variant="primary" size="lg" onClick={() => dispatch(kitWorkRestarted())} data-testid="kit-again">
          {kitAgainLabel(visit.result.kind, visit.result.sku)}
        </Button>
      </>
    );
  }

  return (
    <div className="space-y-5" data-testid="kit-detail" data-kit-id={kit.id} data-mode={mode} data-step={step}>
      <KitHeader kit={kit} />

      <OperatorTabs
        tabs={MODES.map((value) => ({
          value,
          label: KIT_WORK_LABELS[value],
          disabled: locked || (value === KIT_WORK.DISASSEMBLE && Boolean(kit.disassemble_blocked)),
        }))}
        active={mode}
        onChange={(value) => dispatch(kitModeChanged(value))}
        testId="kit-mode-tab"
        dataKey="mode"
        label="Qué hacer con el kit"
      />
      {blocked && (
        <p data-testid="kit-disassemble-blocked" className="flex items-start gap-2 text-sm text-text-secondary">
          <Ban size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          {blocked}
        </p>
      )}

      {error && (
        <div data-testid="kit-reload-error" role="alert">
          <ErrorState variant="inline" title="No pudimos recargar el kit; puede no estar al día." description={error.message}>
            <PendingButton
              pendingOn={fetchKit}
              pendingKey={kitId}
              variant="secondary"
              size="lg"
              fullWidth
              icon={RefreshCw}
              loadingLabel="Recargando…"
              onClick={() => dispatch(fetchKit(kitId))}
              data-testid="kit-reload-error-retry"
            >
              Recargar el kit
            </PendingButton>
          </ErrorState>
        </div>
      )}

      {unconfirmed && (
        <p
          data-testid="kit-unconfirmed"
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-[#2a2114] dark:text-amber-300"
        >
          <AlertTriangle size={20} aria-hidden="true" className="shrink-0" />
          No sabemos si se registró. Reintenta antes de seguir: no se registra dos veces.
        </p>
      )}

      {content}
      {bar && <OperatorActionBar>{bar}</OperatorActionBar>}

      <ConfirmSheet
        isOpen={confirming && step === KIT_STEP.DESTINATION && !unconfirmed && !kitDestinationProblem(visit)}
        title={`¿${kitActionText(mode, visit.quantity, sku)}?`}
        message={kitConfirmMessage(mode, destination)}
        items={confirmItems(visit)}
        confirmLabel={kitActionText(mode, visit.quantity, sku)}
        busy={Boolean(pending)}
        busyLabel={busyLabelOf(mode)}
        onConfirm={confirm}
        onClose={() => setConfirming(false)}
        testId="kit-confirm-sheet"
        confirmTestId="kit-confirm"
      >
        {mode === KIT_WORK.ASSEMBLE && (kit.lot_controlled || kit.expiry_controlled) && (
          <p className="text-sm text-text-secondary">
            {[
              kit.lot_controlled ? `Lote ${visit.kitLot.lot.trim()}` : null,
              kit.expiry_controlled ? `vence ${visit.kitLot.expiryDate}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </ConfirmSheet>
    </div>
  );
};
